import { Alert, Button, Form, Input, Modal, Spin } from "antd";
import { FormComponentProps } from "antd/lib/form";
import UserStore from "client/Data/UserStore";
import { SessionStorageKeys } from "common/enums";
import { translate } from "common/utils";
import { observer } from "mobx-react";
import * as React from "react";

type Props = {
  visible: boolean;
  isSSO: boolean;
  onReauthenticate: (password: string) => Promise<void>;
  onSSOLogin: () => void;
  onCancel: () => void;
};

type State = {
  loading: boolean;
  error: string;
};

class SessionExpiredModalComponent extends React.Component<Props & FormComponentProps, State> {
  state: State = {
    loading: false,
    error: ""
  };

  handleSubmit = () => {
    const { form, onReauthenticate, onSSOLogin, isSSO } = this.props;

    if (isSSO) {
      onSSOLogin();
      return;
    }

    form.validateFields(async (err, values) => {
      if (err) return;

      this.setState({ loading: true, error: "" });

      try {
        await onReauthenticate(values.password);
        this.setState({ loading: false });
        form.resetFields();
      } catch (e: any) {
        this.setState({
          loading: false,
          error: e.message || translate("apiMessages:login-failed")
        });
      }
    });
  };

  handleCancel = () => {
    this.props.form.resetFields();
    this.setState({ error: "" });
    this.props.onCancel();
  };

  render() {
    const { visible, isSSO, form } = this.props;
    const { loading, error } = this.state;
    const { getFieldDecorator } = form;

    const title = translate("apiMessages:session-expired-modal-title");
    const message = translate("apiMessages:session-expired-modal-message");

    return (
      <Modal
        visible={visible}
        title={title}
        onCancel={this.handleCancel}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        className="session-expired-modal"
        style={{ zIndex: 2001 }}
      >
        <Spin spinning={loading}>
          <p style={{ marginBottom: 16 }}>{message}</p>

          {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

          {!isSSO && (
            <Form layout="vertical">
              <Form.Item label={translate("apiMessages:session-expired-modal-username-label")}>
                <Input
                  value={
                    UserStore.currentUser?.username ||
                    sessionStorage.getItem(SessionStorageKeys.USER_ID) ||
                    ""
                  }
                  disabled
                />
              </Form.Item>
              <Form.Item label={translate("apiMessages:session-expired-modal-password-label")}>
                {getFieldDecorator("password", {
                  rules: [
                    {
                      required: true,
                      message: translate("apiMessages:session-expired-modal-password-required")
                    },
                    {
                      pattern: /^\S+$/,
                      message: translate("apiMessages:session-expired-modal-password-no-whitespace")
                    }
                  ]
                })(
                  <Input.Password
                    placeholder={translate(
                      "apiMessages:session-expired-modal-password-placeholder"
                    )}
                    onPressEnter={this.handleSubmit}
                    autoFocus
                  />
                )}
              </Form.Item>
            </Form>
          )}

          <div className="mt-16" style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={this.handleCancel}>{translate("common:cancel")}</Button>
            <Button type="primary" onClick={this.handleSubmit} loading={loading}>
              {isSSO
                ? translate("apiMessages:session-expired-modal-sso-button")
                : translate("apiMessages:session-expired-modal-relogin-button")}
            </Button>
          </div>
        </Spin>
      </Modal>
    );
  }
}

const SessionExpiredModal = Form.create<Props & FormComponentProps>()(
  observer(SessionExpiredModalComponent)
);

export default SessionExpiredModal;
